import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Users, Search, Loader2, Eye, X, Phone, Mail,
  MapPin, ShoppingBag, DollarSign, CalendarDays, Clock, User,
  ChevronDown, Package, MessageCircle, Gift, RefreshCw, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Order = Tables<"orders">;
type OrderItem = Tables<"order_items">;

interface Customer {
  id: string; // customer_id or generated
  name: string;
  phone: string | null;
  address: string | null;
  totalOrders: number;
  totalSpent: number;
  lastOrderDate: string;
  firstOrderDate: string;
  avgTicket: number;
  orders: (Order & { items: OrderItem[] })[];
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  received: { label: "Recebido", color: "bg-primary/10 text-primary border-primary/30" },
  preparing: { label: "Em Preparo", color: "bg-warning/10 text-warning border-warning/30" },
  ready: { label: "Pronto", color: "bg-success/10 text-success border-success/30" },
  out_for_delivery: { label: "Saiu p/ Entrega", color: "bg-primary/10 text-primary border-primary/30" },
  completed: { label: "Finalizado", color: "bg-muted text-muted-foreground border-muted" },
  cancelled: { label: "Cancelado", color: "bg-destructive/10 text-destructive border-destructive/30" },
};

const ORDER_TYPE_LABELS: Record<string, string> = {
  dine_in: "🍽️ No Local",
  takeout: "🥡 Retirada",
  delivery: "🛵 Delivery",
  scheduled: "📅 Agendado",
};

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
const formatDateTime = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) +
  " " + new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const Clientes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"recent" | "spent" | "orders" | "name">("recent");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantSlug, setRestaurantSlug] = useState<string | null>(null);
  const [reactivationCoupons, setReactivationCoupons] = useState<any[]>([]);
  const [reactivationLoading, setReactivationLoading] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [activeTab, setActiveTab] = useState("clientes");

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("id, slug").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("id, slug").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      setRestaurantSlug(rest.slug);

      const { data: ordersData } = await supabase
        .from("orders").select("*").eq("restaurant_id", rest.id).order("created_at", { ascending: false });

      const allOrders = ordersData || [];
      setOrders(allOrders);

      const orderIds = allOrders.map((o) => o.id);
      if (orderIds.length > 0) {
        const { data: items } = await supabase.from("order_items").select("*").in("order_id", orderIds);
        setOrderItems(items || []);
      }
      setLoading(false);
    };
    init();
  }, [navigate]);

  // Build customers from orders
  const customers = useMemo(() => {
    const map = new Map<string, Customer>();

    orders.forEach((order) => {
      // Group by customer_name + phone as unique key
      const key = (order.customer_name || "Anônimo") + "|" + (order.customer_phone || "");
      const existing = map.get(key);
      const items = orderItems.filter((i) => i.order_id === order.id);
      const total = Number(order.total);
      const orderWithItems = { ...order, items };

      if (existing) {
        existing.totalOrders += 1;
        existing.totalSpent += order.status !== "cancelled" ? total : 0;
        existing.orders.push(orderWithItems);
        if (order.created_at > existing.lastOrderDate) {
          existing.lastOrderDate = order.created_at;
        }
        if (order.created_at < existing.firstOrderDate) {
          existing.firstOrderDate = order.created_at;
        }
        if (order.delivery_address && !existing.address) {
          existing.address = order.delivery_address;
        }
        existing.avgTicket = existing.totalSpent / existing.orders.filter(o => o.status !== "cancelled").length || 0;
      } else {
        map.set(key, {
          id: order.customer_id || key,
          name: order.customer_name || "Anônimo",
          phone: order.customer_phone,
          address: order.delivery_address,
          totalOrders: 1,
          totalSpent: order.status !== "cancelled" ? total : 0,
          lastOrderDate: order.created_at,
          firstOrderDate: order.created_at,
          avgTicket: order.status !== "cancelled" ? total : 0,
          orders: [orderWithItems],
        });
      }
    });

    return Array.from(map.values());
  }, [orders, orderItems]);

  // Filter & sort
  const filtered = useMemo(() => {
    let result = [...customers];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "recent": result.sort((a, b) => b.lastOrderDate.localeCompare(a.lastOrderDate)); break;
      case "spent": result.sort((a, b) => b.totalSpent - a.totalSpent); break;
      case "orders": result.sort((a, b) => b.totalOrders - a.totalOrders); break;
      case "name": result.sort((a, b) => a.name.localeCompare(b.name)); break;
    }

    return result;
  }, [customers, search, sortBy]);

  // Stats
  const stats = useMemo(() => ({
    totalCustomers: customers.length,
    totalRevenue: customers.reduce((s, c) => s + c.totalSpent, 0),
    avgTicket: customers.length > 0
      ? customers.reduce((s, c) => s + c.totalSpent, 0) / orders.filter(o => o.status !== "cancelled").length || 0
      : 0,
    returning: customers.filter((c) => c.totalOrders > 1).length,
  }), [customers, orders]);

  const openDetail = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailOpen(true);
  };

  const handleCheckInactive = async () => {
    if (!restaurantId) return;
    setReactivationLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-inactive-customers", {
        body: {
          restaurant_id: restaurantId,
          discount_value: 10,
          discount_type: "percentage",
          valid_days: 7,
        },
      });
      if (error) throw error;
      setReactivationCoupons(data.coupons || []);
      if (data.generated_count > 0) {
        toast.success(`${data.generated_count} cupom(ns) gerado(s) para clientes inativos!`);
      } else if (data.inactive_count === 0) {
        toast.info("Nenhum cliente inativo há mais de 30 dias encontrado.");
      } else {
        toast.info("Todos os clientes inativos já possuem cupons ativos.");
      }
    } catch (err) {
      toast.error("Erro ao verificar clientes inativos.");
    }
    setReactivationLoading(false);
  };

  const menuLink = restaurantSlug ? `https://viamenu.lovable.app/${restaurantSlug}` : "";

  const buildWhatsAppMessage = (couponCode: string, value: number, type: string) => {
    const discountText = type === "percentage" ? `${value}%` : `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
    return `Olá! 😊 Sentimos sua falta! 🍽️\n\nPreparamos um cupom especial de *${discountText} de desconto* para você voltar a pedir conosco!\n\n🎁 Seu cupom: *${couponCode}*\n\nÉ só usar no próximo pedido pelo nosso cardápio digital:\n👉 ${menuLink}\n\nTe esperamos! 💛`;
  };

  const buildWhatsAppLink = (phone: string, couponCode: string, value: number, type: string) => {
    const cleanPhone = phone.replace(/\D/g, "");
    const fullPhone = cleanPhone.startsWith("55") ? cleanPhone : `55${cleanPhone}`;
    const msg = encodeURIComponent(buildWhatsAppMessage(couponCode, value, type));
    return `https://wa.me/${fullPhone}?text=${msg}`;
  };

  const handleBulkWhatsAppSend = async () => {
    if (!restaurantId) return;
    const pendingCoupons = reactivationCoupons.filter(
      (c) => c.phone && c.uses_count === 0 && (!c.valid_until || new Date(c.valid_until) >= new Date())
    );
    if (pendingCoupons.length === 0) {
      toast.info("Nenhum cupom pendente com telefone para enviar.");
      return;
    }
    setBulkSending(true);
    let sent = 0;
    let failed = 0;
    for (const item of pendingCoupons) {
      try {
        const message = buildWhatsAppMessage(item.coupon_code, item.value, item.type);
        const { error } = await supabase.functions.invoke("whatsapp-send", {
          body: {
            restaurant_id: restaurantId,
            phone: item.phone,
            message,
          },
        });
        if (error) { failed++; } else { sent++; }
      } catch {
        failed++;
      }
    }
    setBulkSending(false);
    if (sent > 0) toast.success(`${sent} mensagem(ns) enviada(s) via WhatsApp!`);
    if (failed > 0) toast.error(`${failed} mensagem(ns) falharam. Verifique a conexão do WhatsApp.`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Users className="h-5 w-5 text-primary" />
            <h1 className="font-display font-bold text-lg">Clientes</h1>
            <Badge variant="secondary" className="text-xs">{filtered.length}</Badge>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total de Clientes", value: String(stats.totalCustomers), icon: Users, accent: "text-foreground" },
            { label: "Recorrentes", value: String(stats.returning), icon: CalendarDays, accent: "text-primary" },
            { label: "Faturamento Total", value: formatCurrency(stats.totalRevenue), icon: DollarSign, accent: "text-success" },
            { label: "Ticket Médio", value: formatCurrency(stats.avgTicket), icon: ShoppingBag, accent: "text-warning" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-gradient-card shadow-card p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`h-4 w-4 ${s.accent}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className={`text-xl font-bold font-display ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full max-w-sm">
            <TabsTrigger value="clientes" className="flex-1 gap-1.5">
              <Users className="h-4 w-4" /> Clientes
            </TabsTrigger>
            <TabsTrigger value="reativacao" className="flex-1 gap-1.5">
              <Gift className="h-4 w-4" /> Reativação
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clientes" className="space-y-5 mt-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome, telefone, endereço..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10 bg-card border-border"
                />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                <SelectTrigger className="w-[160px] h-10 bg-card border-border">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Mais recentes</SelectItem>
                  <SelectItem value="spent">Maior gasto</SelectItem>
                  <SelectItem value="orders">Mais pedidos</SelectItem>
                  <SelectItem value="name">Nome A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer list */}
            {filtered.length === 0 ? (
              <div className="rounded-xl border border-border bg-gradient-card shadow-card p-12 text-center">
                <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">Nenhum cliente encontrado</p>
                <p className="text-xs text-muted-foreground mt-1">Os clientes aparecerão aqui quando realizarem pedidos</p>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-muted-foreground border-b border-border bg-secondary/30">
                        <th className="text-left p-3 font-medium">Cliente</th>
                        <th className="text-left p-3 font-medium hidden sm:table-cell">Contato</th>
                        <th className="text-left p-3 font-medium">Pedidos</th>
                        <th className="text-left p-3 font-medium">Gasto Total</th>
                        <th className="text-left p-3 font-medium hidden md:table-cell">Ticket Médio</th>
                        <th className="text-left p-3 font-medium hidden lg:table-cell">Último Pedido</th>
                        <th className="text-right p-3 font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence>
                        {filtered.map((customer) => (
                          <motion.tr
                            key={customer.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="border-b border-border/50 hover:bg-secondary/20 transition-colors cursor-pointer"
                            onClick={() => openDetail(customer)}
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <User className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{customer.name}</p>
                                  {customer.totalOrders > 1 && (
                                    <span className="text-[10px] text-primary font-medium">Recorrente</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3 hidden sm:table-cell">
                              <div className="text-sm text-muted-foreground">
                                {customer.phone ? (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" /> {customer.phone}
                                  </span>
                                ) : (
                                  <span className="text-xs">—</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-sm font-medium">{customer.totalOrders}</span>
                            </td>
                            <td className="p-3">
                              <span className="text-sm font-medium text-success">{formatCurrency(customer.totalSpent)}</span>
                            </td>
                            <td className="p-3 hidden md:table-cell">
                              <span className="text-sm text-muted-foreground">{formatCurrency(customer.avgTicket)}</span>
                            </td>
                            <td className="p-3 hidden lg:table-cell">
                              <span className="text-xs text-muted-foreground">{formatDate(customer.lastOrderDate)}</span>
                            </td>
                            <td className="p-3 text-right">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(customer); }}>
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="reativacao" className="space-y-5 mt-4">
            <div className="rounded-xl border border-border bg-gradient-card shadow-card p-5 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-display font-bold text-base flex items-center gap-2">
                    <Gift className="h-5 w-5 text-primary" />
                    Reativação de Clientes
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Identifique clientes que não pedem há mais de 30 dias e envie cupons de desconto via WhatsApp
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={handleCheckInactive}
                    disabled={reactivationLoading}
                    className="gap-2"
                  >
                    {reactivationLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    Verificar Inativos
                  </Button>
                  {reactivationCoupons.length > 0 && (
                    <Button
                      onClick={handleBulkWhatsAppSend}
                      disabled={bulkSending}
                      variant="outline"
                      className="gap-2 border-[hsl(142,71%,45%)] text-[hsl(142,71%,35%)] hover:bg-[hsl(142,71%,95%)]"
                    >
                      {bulkSending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Enviar Todos via WhatsApp
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Cupons gerados com 10% de desconto e validade de 7 dias. Para alterar valores, edite os cupons no painel de descontos.
              </p>
            </div>

            {reactivationCoupons.length === 0 ? (
              <div className="rounded-xl border border-border bg-gradient-card shadow-card p-12 text-center">
                <MessageCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground font-medium">Nenhum cupom de reativação gerado</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Clique em "Verificar Inativos" para identificar clientes e gerar cupons automaticamente
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-gradient-card shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-xs text-muted-foreground border-b border-border bg-secondary/30">
                        <th className="text-left p-3 font-medium">Cliente</th>
                        <th className="text-left p-3 font-medium">Telefone</th>
                        <th className="text-left p-3 font-medium">Cupom</th>
                        <th className="text-left p-3 font-medium">Desconto</th>
                        <th className="text-left p-3 font-medium hidden sm:table-cell">Validade</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-right p-3 font-medium">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reactivationCoupons.map((item) => {
                        const isExpired = item.valid_until && new Date(item.valid_until) < new Date();
                        const isUsed = item.uses_count > 0;
                        const discountText = item.type === "percentage"
                          ? `${item.value}%`
                          : formatCurrency(Number(item.value));
                        return (
                          <motion.tr
                            key={item.coupon_id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="border-b border-border/50 hover:bg-secondary/20 transition-colors"
                          >
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
                                  <User className="h-4 w-4 text-warning" />
                                </div>
                                <span className="text-sm font-medium">{item.name}</span>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="text-sm text-muted-foreground">{item.phone || "—"}</span>
                            </td>
                            <td className="p-3">
                              <code className="text-xs font-mono bg-secondary px-2 py-1 rounded">
                                {item.coupon_code}
                              </code>
                            </td>
                            <td className="p-3">
                              <Badge variant="secondary" className="text-xs">{discountText}</Badge>
                            </td>
                            <td className="p-3 hidden sm:table-cell">
                              <span className="text-xs text-muted-foreground">
                                {item.valid_until ? formatDate(item.valid_until) : "—"}
                              </span>
                            </td>
                            <td className="p-3">
                              {isUsed ? (
                                <Badge className="bg-success/10 text-success border-success/30 text-[10px]">Usado</Badge>
                              ) : isExpired ? (
                                <Badge variant="destructive" className="text-[10px]">Expirado</Badge>
                              ) : (
                                <Badge className="bg-warning/10 text-warning border-warning/30 text-[10px]">Pendente</Badge>
                              )}
                            </td>
                            <td className="p-3 text-right">
                              {item.phone && !isUsed && !isExpired && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="gap-1.5 text-xs"
                                  onClick={() => window.open(buildWhatsAppLink(item.phone, item.coupon_code, item.value, item.type), "_blank")}
                                >
                                  <Send className="h-3.5 w-3.5" />
                                  WhatsApp
                                </Button>
                              )}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-display">{selectedCustomer.name}</p>
                    <p className="text-xs text-muted-foreground font-normal">
                      Cliente desde {formatDate(selectedCustomer.firstOrderDate)}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Contact info */}
              <div className="space-y-2 mt-2">
                {selectedCustomer.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${selectedCustomer.phone}`} className="hover:text-foreground transition-colors">
                      {selectedCustomer.phone}
                    </a>
                  </div>
                )}
                {selectedCustomer.address && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span>{selectedCustomer.address}</span>
                  </div>
                )}
              </div>

              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className="text-lg font-bold font-display">{selectedCustomer.totalOrders}</p>
                  <p className="text-[11px] text-muted-foreground">Pedidos</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className="text-lg font-bold font-display text-success">{formatCurrency(selectedCustomer.totalSpent)}</p>
                  <p className="text-[11px] text-muted-foreground">Total Gasto</p>
                </div>
                <div className="rounded-lg border border-border p-3 text-center">
                  <p className="text-lg font-bold font-display">{formatCurrency(selectedCustomer.avgTicket)}</p>
                  <p className="text-[11px] text-muted-foreground">Ticket Médio</p>
                </div>
              </div>

              {/* Order history */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
                  <Package className="h-4 w-4 text-primary" />
                  Histórico de Pedidos
                </h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {selectedCustomer.orders
                    .sort((a, b) => b.created_at.localeCompare(a.created_at))
                    .map((order) => {
                      const st = STATUS_LABELS[order.status] || { label: order.status, color: "" };
                      return (
                        <div key={order.id} className="rounded-lg border border-border p-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground">
                                #{order.id.slice(0, 6).toUpperCase()}
                              </span>
                              <Badge variant="outline" className={`text-[10px] ${st.color}`}>
                                {st.label}
                              </Badge>
                            </div>
                            <span className="text-sm font-bold">{formatCurrency(Number(order.total))}</span>
                          </div>
                          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {formatDateTime(order.created_at)}
                            </span>
                            <span>{ORDER_TYPE_LABELS[order.order_type] || order.order_type}</span>
                          </div>
                          {order.items.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {order.items.map((item) => (
                                <span key={item.id} className="mr-2">
                                  {item.quantity}x {item.product_name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Clientes;
